import { CalendarIcon, XIcon } from '@page/icons';
import { VariantProps } from 'tailwind-variants';

import { IconButton } from '../IconButton';
import { cn } from '../../lib/styles';
import { FieldError, FieldGroup, fieldStyles } from '../Field';
import { Label } from '../Label';
import { Calendar } from '../Calendar';
import { Popover, PopoverContent, PopoverContentProps, PopoverTrigger } from '../Popover';
import { useDatePickerContext } from './DateInput.utils';
import { DateInputSegments } from './DateInputSegments';

export const DATE_INPUT_CALENDAR_ICON_CLASSNAME = 'date-input-calendar-icon';
export const DATE_INPUT_CALENDAR_POPOVER_CLASSNAME = 'date-input-calendar-popover';

interface DateInputInnerProps extends VariantProps<typeof fieldStyles> {
  label?: string;
  name?: string;

  error?: string;
  disabled?: boolean;
  clearable?: boolean;
  reserveErrorSpace?: boolean;

  className?: string;
  labelClassName?: string;
  inputClassName?: string;

  popoverContentProps?: PopoverContentProps;

  onBlur?: (event: React.FocusEvent, errors: string[]) => void;
  onClear?: () => void;
}

export function DateInputInner({
  label,
  name,

  error,
  disabled,
  clearable,
  reserveErrorSpace = true,

  size,
  variant,

  className,
  labelClassName,
  inputClassName,

  popoverContentProps,

  onBlur,
  onClear,
}: DateInputInnerProps) {
  const state = useDatePickerContext();

  const renderRightAction = () => {
    const shouldShowClearIcon = clearable && state.value;

    return (
      <Popover {...(shouldShowClearIcon ? { open: false } : {})}>
        <PopoverTrigger asChild>
          <IconButton
            size="sm"
            disabled={disabled}
            className={DATE_INPUT_CALENDAR_ICON_CLASSNAME}
            onClick={shouldShowClearIcon ? onClear : undefined}
            aria-label={shouldShowClearIcon ? 'Clear date' : 'Open calendar'}
          >
            {shouldShowClearIcon ? <XIcon className="size-4 min-w-4" /> : <CalendarIcon className="min-w-5 size-5" />}
          </IconButton>
        </PopoverTrigger>

        <PopoverContent
          {...popoverContentProps}
          className={cn(DATE_INPUT_CALENDAR_POPOVER_CLASSNAME, popoverContentProps?.className)}
        >
          <Calendar />
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <>
      {label && (
        <Label className={cn('mb-2', labelClassName)} htmlFor={name}>
          {label}
        </Label>
      )}

      <FieldGroup
        className={cn(
          'justify-between w-auto cursor-text outline outline-transparent transition-all',
          'disabled:cursor-default disabled:pointer-events-none disabled:**:pointer-events-none disabled:opacity-50',
          {
            'outline-text-destructive': error,
            'focus-within:outline-border-input-select': !error && !disabled,
          },
          className,
        )}
        size={size}
        variant={variant}
        isDisabled={disabled}
        onBlurCapture={(event) => {
          if (event.currentTarget.contains(event.relatedTarget)) return;

          onBlur?.(event, state.realtimeValidation.validationErrors);
        }}
      >
        <DateInputSegments className={cn('flex-1', inputClassName)} />

        {renderRightAction()}
      </FieldGroup>

      {(label && reserveErrorSpace) || error ? <FieldError>{error}</FieldError> : null}
    </>
  );
}
